"use client";

import { useI18n } from "@/components/i18n-provider";
import type { ReviewLanguage } from "@/lib/cv/review-language";
import { cn } from "@/lib/utils";

interface CvReviewLanguageFieldProps {
  value: ReviewLanguage;
  onChange: (value: ReviewLanguage) => void;
  /** Keeps ids unique when the desktop and mobile forms share a page. */
  idPrefix: string;
  /** Matches the label and control scale of the surrounding form. */
  size: "desktop" | "mobile";
}

/**
 * Picks the language of every review output. It arrives preselected from the
 * page locale, so it is drawn as a compact inline switch rather than another
 * full-width choice: the required talent-pool question below keeps the visual
 * weight. Native radios stay in the DOM for arrow keys and screen readers.
 */
export function CvReviewLanguageField({
  value,
  onChange,
  idPrefix,
  size,
}: CvReviewLanguageFieldProps) {
  const { dictionary } = useI18n();
  const copy = dictionary.cvUploadForm.fields.reviewLanguage;
  const labelId = `${idPrefix}-review-language-label`;
  const captionId = `${idPrefix}-review-language-caption`;
  const options: Array<{ value: ReviewLanguage; label: string }> = [
    { value: "Indonesian", label: copy.indonesian },
    { value: "English", label: copy.english },
  ];

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      aria-describedby={captionId}
      className="space-y-1.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p
          id={labelId}
          className={cn(
            "font-medium text-text-heading",
            size === "desktop" ? "text-base leading-6" : "text-sm leading-5",
          )}
        >
          {copy.label}
        </p>

        <div className="inline-flex shrink-0 rounded-lg border border-border-input bg-surface-primary p-0.5 shadow-button">
          {options.map((option) => {
            const id = `${idPrefix}-review-language-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={id}
                className={cn(
                  "flex cursor-pointer items-center justify-center rounded-md px-3 text-sm font-medium text-text-muted transition-colors",
                  size === "desktop" ? "h-9" : "h-10",
                  "hover:text-text-heading",
                  "has-[:checked]:bg-status-error-bg has-[:checked]:text-text-heading has-[:checked]:ring-1 has-[:checked]:ring-brand-primary",
                  "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-primary",
                )}
              >
                <input
                  id={id}
                  type="radio"
                  name={`${idPrefix}-review-language`}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                  className="sr-only"
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </div>

      <p id={captionId} className="text-xs leading-5 text-text-body">
        {copy.helper}
      </p>
    </div>
  );
}
